import {generateUrl} from "../utils";

const { default: axios } = require("axios")
const qs = require('qs')
import MessageNotificator from "./MessageNotificator";

export class HttpClient {

    // Routes that resolve the project from the uuid in the path (host-agnostic) — stay on the current host.
    // Every other project-scoped request resolves the project from the subdomain (alias) and must be rewritten to it.
    static HOST_AGNOSTIC_PREFIXES = ['/projects/', '/my/', '/integrations/', '/profile', '/proxy/'];

    notFoundHandler: (() => void) | null;

    constructor() {
        this.notificator = new MessageNotificator()

        // Current project alias; when set, legacy requests go to https://{alias}.domain/...
        this.projectSubdomain = null;

        // Invoked on any 404 response, letting the app redirect to its NotFoundPage
        // instead of every caller having to check the status itself.
        this.notFoundHandler = null;

        this.api = axios.create();

        this.api.interceptors.request.use((config) => {
            if (this.#shouldUseProjectSubdomain(config.url)) {
                let path = config.url.replace(/^\/+/, '');

                config.url = generateUrl(path, this.projectSubdomain);
                config.withCredentials = true;
            }

            return config;
        });

        this.api.interceptors.response.use(
            (response) => response,
            (error) => {
                let status = error?.response?.status;

                if (status) {
                    if (status === 401) {
                        window.location.href = generateUrl('app/authentication')
                    } else if (status === 404 && error.config?.skipNotFoundRedirect) {
                        // The caller handles the 404 itself (e.g. shows its own message).
                    } else if (status === 404 && this.notFoundHandler) {
                        this.notFoundHandler();
                    } else if (!error.config?.skipErrorNotification) {
                        this.#showExceptions(error);
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    setNotFoundHandler(handler: () => void) {
        this.notFoundHandler = handler;
    }

    setProjectSubdomain(alias: string) {
        this.projectSubdomain = alias;
    }

    clearProjectSubdomain() {
        this.projectSubdomain = null;
    }

    #shouldUseProjectSubdomain(url: string): boolean {
        if (!this.projectSubdomain || !url || /^https?:\/\//i.test(url)) {
            return false;
        }

        let isHostAgnostic = HttpClient.HOST_AGNOSTIC_PREFIXES.some(prefix => url.startsWith(prefix));

        return !isHostAgnostic;
    }

    get(url: string, params?: object, withCredentials: boolean = false, responseType?: string, config: object = {}) {
        return this.api.get(url, {
            ...config,
            params: params,
            paramsSerializer: params => {
                return qs.stringify(params)
            },
            withCredentials: withCredentials,
            ...(responseType ? {responseType: responseType} : {}),
        });
    }

    post(url: string, params?: object, withCredentials: boolean = false, headers?: object = null) {
        let requestHeaders = {
            withCredentials: withCredentials,
        }

        if (headers !== null) {
            requestHeaders = {
                ...requestHeaders,
                ...headers,
            };
        }

        return this.api.post(url, params, requestHeaders);
    }

    patch(url: string, params: object) {
        return this.api.patch(url, params);
    }

    delete(url: string, params?: object) {
        return this.api.delete(url, {data: params});
    }

    put(url: string, params: object) {
        return this.api.put(url, params);
    }
    #showExceptions(error)
    {
        let response = error.response;

        let message = response?.data?.message;

        if (message === undefined) {
            return;
        }

        let errors = [];

        for (const [key, value] of Object.entries(response?.data?.errors ?? {})) {
            errors.push(value)
        }

        let html = document.createElement('div')

        if (errors.length > 0) {
            errors.forEach((item) => {
                let li = document.createElement("li");
                li.innerText = item;
                html.appendChild(li);
            });
        } else {
            let li = document.createElement("li");
            li.innerText = message;
            html.appendChild(li);
        }

        this.notificator.warning(html.outerHTML);
    }
}

export const httpClient = new HttpClient()
